import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X, Upload, Image, ChevronUp, ChevronDown, GripVertical, ExternalLink, Settings2, Maximize2, LayoutGrid, Calendar, Move } from 'lucide-react';
import { databaseService, storageService, ID } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';

const GALLERIES_COLLECTION = 'gallery_carousels';
const GALLERY_BUCKET = 'gallery_images';

// Label design presets - 15 unique designs
const labelDesigns = [
  { id: 'vintage-paper', name: 'Vintage Paper', preview: 'bg-[#f5f0e1] text-[#2d8a8a] italic shadow-md' },
  { id: 'tape-label', name: 'Tape Label', preview: 'bg-[#f5f0e1] text-[#2d8a8a] italic' },
  { id: 'ribbon', name: 'Classic Ribbon', preview: 'bg-white text-gray-800 italic shadow-lg' },
  { id: 'neon-glow', name: 'Neon Glow', preview: 'bg-black text-[#00ff88] border border-[#00ff88]' },
  { id: 'retro-badge', name: 'Retro Badge', preview: 'bg-[#d4a574] text-[#3d2914] border-2 border-[#3d2914]' },
  { id: 'glass-card', name: 'Glass Card', preview: 'bg-white/30 text-white backdrop-blur' },
  { id: 'chalk-board', name: 'Chalkboard', preview: 'bg-[#2a3d2a] text-[#e8e8e8] border border-[#e8e8e8]' },
  { id: 'polaroid', name: 'Polaroid', preview: 'bg-white text-gray-700 shadow-xl' },
  { id: 'stamp', name: 'Stamp', preview: 'bg-transparent text-red-600 border-2 border-red-600 border-dashed' },
  { id: 'typewriter', name: 'Typewriter', preview: 'bg-[#f9f6f0] text-gray-800 font-mono' },
  { id: 'cinema', name: 'Cinema', preview: 'bg-black text-[#ffd700] border-y-2 border-[#ffd700]' },
  { id: 'wooden', name: 'Wooden Sign', preview: 'bg-[#8b5a2b] text-[#f5deb3] shadow-lg' },
  { id: 'neon-pink', name: 'Neon Pink', preview: 'bg-black text-[#ff1493] border border-[#ff1493]' },
  { id: 'newspaper', name: 'Newspaper', preview: 'bg-[#f5f5dc] text-gray-900 font-serif' },
  { id: 'gradient-modern', name: 'Modern Gradient', preview: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' },
];

const emptyGallery = {
  title: '',
  eventLink: '',
  images: [],
  labelDesign: 'ribbon',
  titleFontSize: '14px',
  titleColor: '#333333',
  scrollInterval: 3000,
  featured: true,
  order: 0,
  aspectRatio: 'aspect-[4/3]',
};

export default function GalleryManagement() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyGallery);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImageTitle, setNewImageTitle] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    settings: true,
    design: true,
    images: true
  });
  const [editingImage, setEditingImage] = useState(null); // { index, data }
  const [imageCropData, setImageCropData] = useState({ zoom: 1, x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, startX: 50, startY: 50 });
  const { showToast } = useToast();

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      const result = await databaseService.listDocuments(GALLERIES_COLLECTION);
      if (result.success) {
        const parsed = result.data.documents.map(g => ({
          ...g,
          images: typeof g.images === 'string' ? JSON.parse(g.images || '[]') : (g.images || [])
        }));
        const sorted = parsed.sort((a, b) => (a.order || 0) - (b.order || 0));
        setGalleries(sorted);
      }
    } catch (error) {
      console.error('Error loading galleries:', error);
      showToast('Failed to load galleries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (gallery = null) => {
    if (gallery) {
      setEditingGallery(gallery);
      setFormData({
        ...emptyGallery,
        ...gallery,
        images: typeof gallery.images === 'string' ? JSON.parse(gallery.images || '[]') : (gallery.images || []),
      });
    } else {
      setEditingGallery(null);
      setFormData({ ...emptyGallery, order: galleries.length });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGallery(null);
    setFormData(emptyGallery);
    setNewImageTitle('');
    setExpandedSections({ settings: true, design: true, images: true });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    try {
      const uploadedImages = [];
      for (const file of files) {
        const fileId = ID.unique();
        const uploadResult = await storageService.uploadFile(GALLERY_BUCKET, fileId, file);
        if (uploadResult.success) {
          const imageUrl = storageService.getFileView(GALLERY_BUCKET, uploadResult.data.$id);
          uploadedImages.push({
            id: uploadResult.data.$id,
            url: imageUrl,
            title: newImageTitle || '',
            zoom: 1,
            x: 50,
            y: 50
          });
        }
      }
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
      setNewImageTitle('');
      showToast(`${uploadedImages.length} image(s) uploaded`, 'success');
    } catch (error) {
      console.error('Error uploading images:', error);
      showToast('Failed to upload images', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const updateImageTitle = (index, title) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? { ...img, title } : img)
    }));
  };

  const openImageEditor = (index) => {
    const img = formData.images[index];
    setEditingImage({ index, ...img });
    setImageCropData({
      zoom: img.zoom || 1,
      x: img.x || 50,
      y: img.y || 50
    });
  };

  const saveImageCrop = () => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => 
        i === editingImage.index 
          ? { ...img, ...imageCropData }
          : img
      )
    }));
    setEditingImage(null);
    showToast('Image adjustment applied temporarily. Remember to save the gallery.', 'info');
  };

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      startX: imageCropData.x,
      startY: imageCropData.y
    };
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const zoomFactor = Math.max(0.1, (imageCropData.zoom || 1) - 1);
    const sensitivity = 0.15 / zoomFactor; 
    
    setImageCropData(prev => ({
      ...prev,
      x: Math.max(0, Math.min(100, dragStartRef.current.startX - dx * sensitivity)),
      y: Math.max(0, Math.min(100, dragStartRef.current.startY - dy * sensitivity))
    }));
  };

  const handleDragEnd = () => setIsDragging(false);

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveImage = (index, direction) => {
    const newImages = [...formData.images];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newImages.length) return;
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast('Gallery title is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const galleryData = {
        title: formData.title.trim(),
        eventLink: formData.eventLink.trim(),
        images: JSON.stringify(formData.images.map(img => ({ ...img, title: (img.title || '').trim() }))),
        labelDesign: formData.labelDesign,
        titleFontSize: formData.titleFontSize,
        titleColor: formData.titleColor,
        scrollInterval: parseInt(formData.scrollInterval) || 3000,
        featured: formData.featured,
        order: formData.order,
        aspectRatio: formData.aspectRatio || 'aspect-[4/3]',
      };

      if (editingGallery) {
        const result = await databaseService.updateDocument(
          GALLERIES_COLLECTION,
          editingGallery.$id,
          galleryData
        );
        if (result.success) {
          showToast('Gallery updated successfully', 'success');
          loadGalleries();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      } else {
        const result = await databaseService.createDocument(
          GALLERIES_COLLECTION,
          galleryData
        );
        if (result.success) {
          showToast('Gallery created successfully', 'success');
          loadGalleries();
          closeModal();
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      console.error('Error saving gallery:', error);
      showToast('Failed to save gallery', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (gallery) => {
    if (!window.confirm(`Delete "${gallery.title}"? This cannot be undone.`)) return;

    try {
      const result = await databaseService.deleteDocument(GALLERIES_COLLECTION, gallery.$id);
      if (result.success) {
        showToast('Gallery deleted', 'success');
        loadGalleries();
      }
    } catch (error) {
      console.error('Error deleting gallery:', error);
      showToast('Failed to delete gallery', 'error');
    }
  };

  const toggleFeatured = async (gallery) => {
    try {
      const result = await databaseService.updateDocument(
        GALLERIES_COLLECTION,
        gallery.$id,
        { featured: !gallery.featured }
      );
      if (result.success) {
        showToast(`Gallery ${!gallery.featured ? 'featured' : 'hidden'}`, 'success');
        loadGalleries();
      }
    } catch (error) {
      showToast('Failed to update', 'error');
    }
  };

  const moveGallery = async (index, direction) => {
    const newGalleries = [...galleries];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newGalleries.length) return;

    [newGalleries[index], newGalleries[targetIndex]] = [newGalleries[targetIndex], newGalleries[index]];

    try {
      await Promise.all([
        databaseService.updateDocument(GALLERIES_COLLECTION, newGalleries[index].$id, { order: index }),
        databaseService.updateDocument(GALLERIES_COLLECTION, newGalleries[targetIndex].$id, { order: targetIndex })
      ]);
      loadGalleries();
    } catch (error) {
      showToast('Failed to reorder', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#4C1D95' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Project Gallery</h1>
          <p className="text-gray-500 mt-1">Manage, organize, and showcase your project image carousels.</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#4C1D95] text-white rounded-lg hover:opacity-90 transition-all shadow-md hover:shadow-lg active:scale-95"
        >
          <Plus className="w-5 h-5" />
          Create New Gallery
        </button>
      </div>

      {/* Gallery Grid */}
      {galleries.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center border-2 border-dashed border-gray-200">
          <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <LayoutGrid className="w-10 h-10 text-[#4C1D95]" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No galleries created yet</h3>
          <p className="text-gray-500 mb-8 max-w-sm mx-auto">Start by creating your first image carousel gallery to showcase your work.</p>
          <button
            onClick={() => openModal()}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Gallery
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {galleries.map((gallery, index) => (
            <div
              key={gallery.$id}
              className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
            >
              {/* Card Header (Preview) */}
              <div className="relative h-48 bg-gray-100 overflow-hidden">
                {gallery.images && gallery.images.length > 0 ? (
                  <>
                    <img
                      src={gallery.images[0].url}
                      alt={gallery.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-300">
                    <Image className="w-12 h-12" />
                  </div>
                )}
                
                {/* Status Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                   {gallery.featured && (
                     <span className="bg-green-500/90 backdrop-blur text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1">
                       <Eye className="w-3 h-3" /> Featured
                     </span>
                   )}
                </div>
                
                 {/* Quick Actions Overlay */}
                 <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0 duration-300">
                    <button 
                      onClick={() => moveGallery(index, 'up')} 
                      disabled={index === 0}
                      className="p-1.5 bg-white/90 backdrop-blur text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                    >
                       <ChevronUp className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => moveGallery(index, 'down')} 
                      disabled={index === galleries.length - 1}
                      className="p-1.5 bg-white/90 backdrop-blur text-gray-700 rounded-lg hover:bg-white disabled:opacity-50"
                    >
                       <ChevronDown className="w-4 h-4" />
                    </button>
                 </div>
              </div>

              {/* Card Content */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-800 line-clamp-1 group-hover:text-[#4C1D95] transition-colors">{gallery.title}</h3>
                  {gallery.eventLink && (
                    <a href={gallery.eventLink} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-[#4C1D95] transition-colors">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Image className="w-4 h-4 text-gray-400" />
                    <span>{gallery.images?.length || 0} Images</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Interval: {gallery.scrollInterval / 1000}s</span>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="mt-auto pt-4 border-t border-gray-100 grid grid-cols-3 gap-3">
                  <button
                    onClick={() => toggleFeatured(gallery)}
                    className={`flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-colors ${
                      gallery.featured 
                        ? 'bg-green-50 text-green-700 hover:bg-green-100' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {gallery.featured ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    {gallery.featured ? 'Visible' : 'Hidden'}
                  </button>
                  <button
                    onClick={() => openModal(gallery)}
                    className="flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-medium transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(gallery)}
                    className="flex items-center justify-center gap-2 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-sm font-medium transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal - Retained Functionality with Updated Styling */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                 <h2 className="text-2xl font-bold text-gray-900">
                  {editingGallery ? 'Edit Gallery' : 'Create New Gallery'}
                </h2>
                <p className="text-sm text-gray-500">Configure your gallery details and images</p>
              </div>
              <button 
                onClick={closeModal} 
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-160px)] custom-scrollbar">
              <div className="space-y-6">
                
                {/* 1. Main Settings */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4C1D95]"></span>
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Gallery Title *</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                        placeholder="e.g., Summer Event 2024"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Event Link (Optional)</label>
                      <input
                        type="url"
                        value={formData.eventLink}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventLink: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>

                <div className="h-px bg-gray-100"></div>

                {/* 2. Customization */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4C1D95]"></span>
                    Display Settings
                  </h3>
                   <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                       <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Interval (ms)</label>
                          <input
                            type="number"
                            value={formData.scrollInterval}
                            onChange={(e) => setFormData(prev => ({ ...prev, scrollInterval: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Font Size</label>
                          <select
                            value={formData.titleFontSize}
                            onChange={(e) => setFormData(prev => ({ ...prev, titleFontSize: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none"
                          >
                            <option value="12px">Small (12px)</option>
                            <option value="14px">Medium (14px)</option>
                            <option value="16px">Large (16px)</option>
                            <option value="18px">Extra Large (18px)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Aspect Ratio</label>
                          <select
                            value={formData.aspectRatio || 'aspect-[4/3]'}
                            onChange={(e) => setFormData(prev => ({ ...prev, aspectRatio: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none"
                          >
                            <option value="aspect-[4/3]">Standard (4:3)</option>
                            <option value="aspect-[1/1]">Square (1:1)</option>
                            <option value="aspect-[3/4]">Portrait (3:4)</option>
                            <option value="aspect-[9/16]">Portrait (9:16)</option>
                          </select>
                        </div>
                         <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Title Color</label>
                          <div className="flex items-center gap-2">
                             <input
                              type="color"
                              value={formData.titleColor}
                              onChange={(e) => setFormData(prev => ({ ...prev, titleColor: e.target.value }))}
                              className="w-full h-11 border border-gray-300 rounded-xl cursor-pointer p-1"
                            />
                          </div>
                        </div>
                   </div>
                </div>
                
                 <div className="space-y-3">
                   <label className="block text-sm font-medium text-gray-700">Label Design Style</label>
                   <div className="grid grid-cols-3 md:grid-cols-5 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                     {labelDesigns.map((design) => (
                        <button
                          key={design.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, labelDesign: design.id }))}
                          className={`group p-2 rounded-lg border-2 transition-all text-left ${
                            formData.labelDesign === design.id
                              ? 'border-[#4C1D95] bg-white ring-2 ring-[#4C1D95]/20 shadow-sm'
                              : 'border-transparent hover:border-gray-200 hover:bg-white'
                          }`}
                        >
                          <div className={`h-8 rounded-md flex items-center justify-center text-[10px] font-medium mb-2 ${design.preview} w-full`}>
                            Prev
                          </div>
                          <p className="text-[10px] text-gray-500 text-center truncate group-hover:text-gray-900">{design.name}</p>
                        </button>
                      ))}
                   </div>
                </div>

                <div className="h-px bg-gray-100"></div>

                {/* 3. Images */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                     <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#4C1D95]"></span>
                      Gallery Images
                    </h3>
                    <label className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium cursor-pointer transition-colors">
                       <Upload className="w-4 h-4" />
                       Upload Images
                       <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                    </label>
                  </div>

                  {uploadingImage && (
                    <div className="flex items-center justify-center p-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4C1D95]"></div>
                    </div>
                  )}

                  {formData.images.length > 0 ? (
                     <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto custom-scrollbar p-1">
                        {formData.images.map((img, index) => (
                           <div key={img.id || index} className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white">
                              <img src={img.url} alt="" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                                <div className="flex justify-end gap-1">
                                   <button type="button" onClick={() => openImageEditor(index)} className="p-1.5 bg-white/90 rounded text-blue-600 hover:bg-white" title="Adjust">
                                      <Settings2 className="w-4 h-4" />
                                   </button>
                                   <button type="button" onClick={() => removeImage(index)} className="p-1.5 bg-white/90 rounded text-red-600 hover:bg-white" title="Remove">
                                      <Trash2 className="w-4 h-4" />
                                   </button>
                                </div>
                                <div className="space-y-1">
                                  <div className="flex gap-1 justify-center">
                                      <button type="button" onClick={() => moveImage(index, 'up')} disabled={index === 0} className="p-1 bg-white/20 hover:bg-white/40 rounded text-white disabled:opacity-30">
                                        <ChevronUp className="w-3 h-3" />
                                      </button>
                                      <button type="button" onClick={() => moveImage(index, 'down')} disabled={index === formData.images.length - 1} className="p-1 bg-white/20 hover:bg-white/40 rounded text-white disabled:opacity-30">
                                        <ChevronDown className="w-3 h-3" />
                                      </button>
                                  </div>
                                   <input
                                    type="text"
                                    value={img.title || ''}
                                    onChange={(e) => updateImageTitle(index, e.target.value)}
                                    placeholder="Caption"
                                    className="w-full px-2 py-1 text-xs bg-white/90 rounded border-none focus:ring-1 focus:ring-blue-500"
                                  />
                                </div>
                              </div>
                              <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 text-white text-[10px] rounded backdrop-blur">
                                #{index + 1}
                              </span>
                           </div>
                        ))}
                     </div>
                  ) : (
                    !uploadingImage && (
                       <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 text-sm">
                         No images uploaded yet.
                       </div>
                    )
                  )}
                </div>

                {/* Featured Checkbox */}
                 <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <input
                      type="checkbox"
                      id="featured-modal"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                      className="w-5 h-5 rounded text-[#4C1D95] focus:ring-[#4C1D95]"
                    />
                    <div>
                      <label htmlFor="featured-modal" className="block text-sm font-semibold text-gray-900">Featured Gallery</label>
                      <p className="text-xs text-gray-500">Enable to show on homepage</p>
                    </div>
                 </div>

              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100 sticky bottom-0 bg-white">
                <button type="button" onClick={closeModal} className="px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-8 py-2.5 bg-[#4C1D95] text-white rounded-xl hover:bg-[#3b1575] font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : (editingGallery ? 'Update Gallery' : 'Create Gallery')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Editor Modal (Preserved Functionality) */}
      {editingImage && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in duration-200">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-[#4C1D95]" />
                Adjust Image Preview
              </h3>
              <button onClick={() => setEditingImage(null)} className="p-1 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-8 flex flex-col items-center">
                <div 
                  className={`w-full max-w-[450px] ${formData.aspectRatio === 'aspect-square' || formData.aspectRatio === 'aspect-[1/1]' ? 'aspect-[1/1] max-w-[400px]' : 'aspect-[4/3]'} rounded-xl overflow-hidden border-4 border-gray-100 shadow-xl bg-gray-50 relative group cursor-move select-none ${isDragging ? 'active:cursor-grabbing' : 'hover:cursor-grab'} transition-all duration-300`}
                  style={{ borderColor: '#4C1D95' }}
                  onMouseDown={handleDragStart}
                  onMouseMove={handleDragMove}
                  onMouseUp={handleDragEnd}
                  onMouseLeave={handleDragEnd}
                >
                  <img 
                    src={editingImage.url} 
                    alt="" 
                    className={`w-full h-full object-cover pointer-events-none ${!isDragging ? 'transition-all duration-300' : ''}`}
                    style={{
                      transform: `scale(${imageCropData.zoom})`,
                      transformOrigin: `${imageCropData.x}% ${imageCropData.y}%`
                    }}
                  />
                  {!isDragging && (
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <div className="bg-white/90 px-4 py-2 rounded-full text-xs font-bold text-gray-800 flex items-center gap-2 shadow-lg">
                        <Move className="w-3 h-3" />
                         Drag to reposition
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Zoom Level</label>
                    <span className="text-xs font-bold text-[#4C1D95]">{imageCropData.zoom}x</span>
                  </div>
                  <input 
                    type="range" 
                    min="1" max="3" step="0.1"
                    value={imageCropData.zoom}
                    onChange={(e) => setImageCropData(prev => ({ ...prev, zoom: parseFloat(e.target.value) }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#4C1D95]"
                  />
                </div>

                <div className="bg-purple-50 p-4 rounded-xl flex gap-3 text-sm text-purple-800">
                  <Maximize2 className="w-5 h-5 shrink-0" />
                  <p>Use the slider to zoom, then drag the image in the preview box above to set the perfect focal point.</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button onClick={() => setEditingImage(null)} className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-lg font-medium transition-colors">Cancel</button>
              <button 
                onClick={saveImageCrop}
                className="px-6 py-2.5 bg-[#4C1D95] text-white rounded-lg font-bold hover:opacity-90 shadow-lg transition-all"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
}
